import React, { useRef, useState, useCallback, useEffect } from 'react';
import { DoodleCanvas, DoodleCanvasRef } from './DoodleCanvas';
import { AIPreviewCanvas } from './AIPreviewCanvas';
import { MinimalToolbar, ToolType } from './MinimalToolbar';
import { generateFromSketch } from '../../lib/imageGeneration';
import type { ModelId } from '../../lib/imageGeneration';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { CHARACTER_ARCHETYPES, ARCHETYPE_LAYER_PROMPTS } from '../../lib/characterArchetypes';
import type { CharacterArchetype } from '../../types';
import {
  ArrowCounterClockwiseIcon,
  ArrowClockwiseIcon,
  DownloadIcon,
  GearIcon,
  LightningIcon,
  ApertureIcon,
  UserIcon,
  PaletteIcon,
} from '@phosphor-icons/react';
import { ProductionBar, ProductionBarDivider } from '../ProductionBar';

// Character reference type
interface CharacterRef {
  id: string;
  name: string;
  snapshot_url: string;
  thumbnail_url?: string;
  archetype?: CharacterArchetype;
  style_preset?: string;
}

// Style presets for generation
const STYLE_PRESETS = [
  { id: 'anime', label: 'Anime', prompt: 'anime style, studio MAPPA, high budget animation, beautiful shading' },
  { id: 'realistic', label: 'Realistic', prompt: 'photorealistic, detailed, professional lighting, cinematic quality' },
  { id: 'comic', label: 'Comic', prompt: 'comic book style, sharp ink lines, vibrant colors, dynamic shading' },
  { id: 'sketch', label: 'Sketch', prompt: 'pencil sketch, detailed linework, artistic, hand-drawn style' },
];

const IDLE_DELAY = 800; // ms - delay before auto-triggering generation (fast feedback)

export const DoodlePage: React.FC = () => {
  const { user } = useAuth();

  // Canvas refs
  const canvasRef = useRef<DoodleCanvasRef>(null);

  // Tool state
  const [activeTool, setActiveTool] = useState<ToolType>('brush');
  const [brushSize, setBrushSize] = useState(15);
  const [brushColor, setBrushColor] = useState('#000000');

  // Canvas state
  const [zoom, setZoom] = useState(1.0);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });

  // Generation state
  const [prompt, setPrompt] = useState('1boy, male focus, black hair, bangs, black gloves, black pants, looking at viewer, short hair');
  const [selectedModel] = useState<ModelId>('prunaai/z-image-turbo-controlnet'); // FP8 ControlNet (works with QwenImageDiffsynthControlnet)
  const [controlStrength, setControlStrength] = useState(0.75);
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiImageUrl, setAiImageUrl] = useState<string | null>(null);

  // Mode state
  const [realTimeMode, setRealTimeMode] = useState(true);
  const [showSettings, setShowSettings] = useState(false);

  // Camera state
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);

  // Character & Style state
  const [characters, setCharacters] = useState<CharacterRef[]>([]);
  const [selectedCharacter, setSelectedCharacter] = useState<CharacterRef | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<string>('anime');
  const [loadingCharacters, setLoadingCharacters] = useState(false);
  const [poseMode, setPoseMode] = useState(false); // When true, extract pose from camera

  // History state
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // Fetch user's characters
  useEffect(() => {
    if (!user) return;

    const fetchCharacters = async () => {
      setLoadingCharacters(true);
      try {
        // Fetch from characters_2d table (new layered system)
        const { data: chars2d, error: err2d } = await supabase
          .from('characters_2d')
          .select('id, name, thumbnail_url, archetype, style_preset')
          .eq('user_id', user.id)
          .eq('is_finalized', true)
          .order('updated_at', { ascending: false });

        if (!err2d && chars2d) {
          setCharacters(chars2d.map(c => ({
            id: c.id,
            name: c.name,
            snapshot_url: c.thumbnail_url || '',
            thumbnail_url: c.thumbnail_url,
            archetype: c.archetype,
            style_preset: c.style_preset,
          })));
        }
      } catch (err) {
        console.error('Failed to fetch characters:', err);
      } finally {
        setLoadingCharacters(false);
      }
    };

    fetchCharacters();
  }, [user]);

  // Auto-enable pose mode when camera + character selected
  useEffect(() => {
    setPoseMode(cameraEnabled && selectedCharacter !== null);
  }, [cameraEnabled, selectedCharacter]);

  // Idle timer for auto-generation
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Update history state
  const updateHistoryState = useCallback(() => {
    if (canvasRef.current) {
      setCanUndo(canvasRef.current.canUndo());
      setCanRedo(canvasRef.current.canRedo());
    }
  }, []);

  // Handle stroke start
  const handleStrokeStart = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  // Handle stroke end - trigger auto-generation after idle delay
  const handleStrokeEnd = useCallback(() => {
    updateHistoryState();

    // Auto-generate if real-time mode is enabled
    if (realTimeMode && !isGenerating) {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }

      idleTimerRef.current = setTimeout(() => {
        triggerGeneration();
      }, IDLE_DELAY);
    }
  }, [realTimeMode, isGenerating, updateHistoryState]);

  // Build prompt based on character and style
  const buildPrompt = useCallback(() => {
    let finalPrompt = prompt;

    // If character selected, use character's style
    if (selectedCharacter) {
      const archetype = selectedCharacter.archetype;
      if (archetype && ARCHETYPE_LAYER_PROMPTS[archetype]) {
        const stylePrompt = ARCHETYPE_LAYER_PROMPTS[archetype].body;
        finalPrompt = `${prompt}, ${stylePrompt}`;
      }
    } else {
      // Use selected style preset
      const style = STYLE_PRESETS.find(s => s.id === selectedStyle);
      if (style) {
        finalPrompt = `${prompt}, ${style.prompt}`;
      }
    }

    return finalPrompt;
  }, [prompt, selectedCharacter, selectedStyle]);

  // Trigger AI generation
  const triggerGeneration = useCallback(async () => {
    if (isGenerating || !canvasRef.current) return;

    setIsGenerating(true);

    try {
      const sketchData = canvasRef.current.getDrawingData();
      const finalPrompt = buildPrompt();

      // Pass pose mode and character info to generation
      const result = await generateFromSketch(sketchData, finalPrompt, {
        controlStrength,
        model: selectedModel,
        aspectRatio: '1:1',
        poseMode: poseMode, // Extract pose from image
        characterRef: selectedCharacter ? {
          id: selectedCharacter.id,
          name: selectedCharacter.name,
          imageUrl: selectedCharacter.snapshot_url,
        } : undefined,
      });

      if (result instanceof Blob) {
        const url = URL.createObjectURL(result);
        if (aiImageUrl && aiImageUrl.startsWith('blob:')) {
          URL.revokeObjectURL(aiImageUrl);
        }
        setAiImageUrl(url);
      } else if (typeof result === 'string') {
        setAiImageUrl(result);
      }
    } catch (error) {
      console.error('Generation failed:', error);
    } finally {
      setIsGenerating(false);
    }
  }, [isGenerating, buildPrompt, controlStrength, selectedModel, aiImageUrl, poseMode, selectedCharacter]);

  // Export high-res image
  const handleExport = useCallback(() => {
    if (aiImageUrl) {
      const link = document.createElement('a');
      link.href = aiImageUrl;
      link.download = `doodle-${Date.now()}.png`;
      link.click();
    }
  }, [aiImageUrl]);

  // Handle clear
  const handleClear = useCallback(() => {
    if (canvasRef.current) {
      canvasRef.current.clearDrawing();
    }
  }, []);

  // Toggle camera
  const handleCameraToggle = useCallback(async () => {
    if (cameraEnabled) {
      // Turn off camera
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
        setCameraStream(null);
      }
      setCameraEnabled(false);
    } else {
      // Turn on camera
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'user',
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        });
        setCameraStream(stream);
        setCameraEnabled(true);
      } catch (err) {
        console.error('Failed to access camera:', err);
        alert('Could not access camera. Please check permissions.');
      }
    }
  }, [cameraEnabled, cameraStream]);

  // Capture photo from camera and generate
  const handleCaptureAndGenerate = useCallback(async () => {
    if (!canvasRef.current) return;
    triggerGeneration();
  }, [triggerGeneration]);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      if (e.key === 'b' || e.key === 'B') {
        setActiveTool('brush');
      } else if (e.key === 'e' || e.key === 'E') {
        setActiveTool('eraser');
      } else if (e.key === ' ') {
        e.preventDefault();
        setActiveTool('pan');
      } else if (e.ctrlKey && e.key === 'z') {
        e.preventDefault();
        canvasRef.current?.undo();
        updateHistoryState();
      } else if (e.ctrlKey && e.key === 'y') {
        e.preventDefault();
        canvasRef.current?.redo();
        updateHistoryState();
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === ' ') {
        setActiveTool('brush');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [updateHistoryState]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (aiImageUrl && aiImageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(aiImageUrl);
      }
    };
  }, [aiImageUrl]);

  return (
    <div className="flex flex-col h-full w-full bg-neutral-950 overflow-hidden">
      {/* Production Bar */}
      <ProductionBar page="doodle">
        {/* Undo/Redo */}
        <button
          onClick={() => { canvasRef.current?.undo(); updateHistoryState(); }}
          disabled={!canUndo}
          className="p-1.5 rounded text-zinc-500 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          title="Undo"
        >
          <ArrowCounterClockwiseIcon className="w-4 h-4" />
        </button>
        <button
          onClick={() => { canvasRef.current?.redo(); updateHistoryState(); }}
          disabled={!canRedo}
          className="p-1.5 rounded text-zinc-500 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          title="Redo"
        >
          <ArrowClockwiseIcon className="w-4 h-4" />
        </button>

        <ProductionBarDivider />

        {/* Auto-gen toggle */}
        <button
          onClick={() => setRealTimeMode(!realTimeMode)}
          className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-all ${
            realTimeMode
              ? 'bg-slate-blue/20 text-slate-blue'
              : 'text-zinc-500 hover:text-white hover:bg-white/10'
          }`}
          title={realTimeMode ? 'Auto-generate ON' : 'Auto-generate OFF'}
        >
          <LightningIcon className="w-3.5 h-3.5" weight={realTimeMode ? 'fill' : 'regular'} />
          Auto
        </button>

        {/* Settings */}
        <button
          onClick={() => setShowSettings(!showSettings)}
          className={`p-1.5 rounded transition-colors ${
            showSettings ? 'text-slate-blue bg-slate-blue/10' : 'text-zinc-500 hover:text-white hover:bg-white/10'
          }`}
          title="Settings"
        >
          <GearIcon className="w-4 h-4" />
        </button>

        {/* Export */}
        <button
          onClick={handleExport}
          disabled={!aiImageUrl}
          className="p-1.5 rounded text-zinc-500 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          title="Export Image"
        >
          <DownloadIcon className="w-4 h-4" />
        </button>
      </ProductionBar>

      {/* Settings Panel (collapsible) */}
      {showSettings && (
        <div className="flex flex-wrap items-center gap-6 px-4 py-3 bg-onyx border-b border-carbon animate-in slide-in-from-top-2 duration-200">
          {/* Control Strength */}
          <div className="flex items-center gap-3">
            <span className="text-xs text-zinc-500 uppercase tracking-wider">Strength</span>
            <input
              type="range"
              min="0.3"
              max="1"
              step="0.05"
              value={controlStrength}
              onChange={(e) => setControlStrength(parseFloat(e.target.value))}
              className="w-32 h-1.5 bg-graphite rounded-full appearance-none cursor-pointer accent-slate-blue"
            />
            <span className="text-xs text-zinc-400 font-mono w-8">{controlStrength.toFixed(2)}</span>
          </div>

          {/* Divider */}
          <div className="w-px h-6 bg-graphite" />

          {/* Character Selector */}
          <div className="flex items-center gap-3">
            <UserIcon className="w-4 h-4 text-zinc-500" />
            <select
              value={selectedCharacter?.id || ''}
              onChange={(e) => {
                const char = characters.find(c => c.id === e.target.value);
                setSelectedCharacter(char || null);
              }}
              className="px-3 py-1.5 bg-zinc-900 border border-graphite rounded-lg text-sm text-white focus:outline-none focus:border-slate-blue/50"
            >
              <option value="">No Character</option>
              {characters.map(char => (
                <option key={char.id} value={char.id}>{char.name}</option>
              ))}
            </select>
            {selectedCharacter && (
              <img
                src={selectedCharacter.thumbnail_url || selectedCharacter.snapshot_url}
                alt={selectedCharacter.name}
                className="w-8 h-8 rounded-lg object-cover border border-graphite"
              />
            )}
          </div>

          {/* Divider */}
          <div className="w-px h-6 bg-graphite" />

          {/* Style Selector (only when no character selected) */}
          {!selectedCharacter && (
            <div className="flex items-center gap-3">
              <PaletteIcon className="w-4 h-4 text-zinc-500" />
              <div className="flex gap-1">
                {STYLE_PRESETS.map(style => (
                  <button
                    key={style.id}
                    onClick={() => setSelectedStyle(style.id)}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                      selectedStyle === style.id
                        ? 'bg-slate-blue text-white'
                        : 'bg-zinc-900 text-zinc-400 hover:text-white border border-graphite'
                    }`}
                  >
                    {style.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Pose Mode Indicator */}
          {poseMode && (
            <>
              <div className="w-px h-6 bg-graphite" />
              <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/20 border border-green-500/30">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-xs text-green-400 font-medium">Pose Transfer Active</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Left: Minimal Toolbar */}
        <MinimalToolbar
          activeTool={activeTool}
          onToolChange={setActiveTool}
          brushSize={brushSize}
          onBrushSizeChange={setBrushSize}
          brushColor={brushColor}
          onBrushColorChange={setBrushColor}
          onClear={handleClear}
          cameraEnabled={cameraEnabled}
          onCameraToggle={handleCameraToggle}
        />

        {/* Canvas Area */}
        <div className="flex-1 flex min-w-0 overflow-hidden relative">
          {/* Left Canvas: User Sketch */}
          <DoodleCanvas
            ref={canvasRef}
            tool={activeTool}
            brushSize={brushSize}
            brushColor={brushColor}
            zoom={zoom}
            panOffset={panOffset}
            onPanChange={setPanOffset}
            onZoomChange={setZoom}
            onStrokeStart={handleStrokeStart}
            onStrokeEnd={handleStrokeEnd}
            className="flex-1 border-r border-carbon"
            lightBackground={!cameraEnabled}
            cameraStream={cameraStream}
          />

          {/* Capture button overlay when camera is active */}
          {cameraEnabled && (
            <button
              onClick={handleCaptureAndGenerate}
              disabled={isGenerating}
              className="absolute left-1/4 bottom-6 -translate-x-1/2 z-10 flex items-center gap-2 px-6 py-3 bg-white/90 hover:bg-white text-black rounded-full shadow-lg transition-all disabled:opacity-50"
            >
              <ApertureIcon className="w-6 h-6" weight="bold" />
              <span className="font-semibold">Capture</span>
            </button>
          )}

          {/* Right Canvas: AI Preview */}
          <AIPreviewCanvas
            imageUrl={aiImageUrl}
            isGenerating={isGenerating}
            zoom={zoom}
            panOffset={panOffset}
            className="flex-1"
            lightBackground
          />
        </div>
      </div>

      {/* Bottom: Prompt Bar */}
      <div className="border-t border-carbon bg-neutral-950">
        <div className="flex items-center gap-3 px-4 py-3">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Describe your character..."
            rows={2}
            className="flex-1 px-4 py-2.5 bg-onyx border border-carbon rounded-xl text-white text-sm placeholder-zinc-600 resize-none focus:outline-none focus:border-slate-blue/50 transition-colors"
          />
          <button
            onClick={triggerGeneration}
            disabled={isGenerating}
            className={`px-6 py-3 rounded-xl text-sm font-semibold transition-all ${
              isGenerating
                ? 'bg-graphite text-zinc-500 cursor-not-allowed'
                : 'bg-slate-blue hover:bg-[#6563e0] text-white shadow-lg shadow-slate-blue/20'
            }`}
          >
            {isGenerating ? 'Generating...' : 'Generate'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DoodlePage;
